import { createFileRoute, Outlet } from "@tanstack/react-router";
import { useEffect } from "react";
import { ProtectedRoute } from "@/components/protected-route";
import { NotFoundInLayout } from "@/components/reusable-components/not-found";
import { AppSidebar } from "@/components/sidebar/app-sidebar-user";
import { SidebarProvider } from "@/components/ui/sidebar";
import { useAuthStore } from "@/stores/useAuthStore";
import { auth } from "@/lib/auth";
import { useOrganizationStore } from "@/stores/useOrganizationStore";
import { useFeatureStore } from "@/stores/useFeatureStore";
import { useOrgSettingsStore } from "@/stores/useOrgSettingsStore";

export const Route = createFileRoute("/supervisor/_layout")({
  beforeLoad: async () => {
    await useFeatureStore.getState().fetchFeatures();
    await useOrgSettingsStore.getState().fetchSettings();
    // No org-setting gate on the area itself: overtime claims reach approvers
    // even with both approval toggles off, and the overtime inbox serves them
    // either way (T-165). Per-page gates handle the leave-only pages.
  },
  component: SupervisorLayout,
  notFoundComponent: NotFoundInLayout,
});

function SupervisorLayout() {
  const { organizationTimezone, fetchTimezone, isLoading, error } =
    useOrganizationStore();

  useEffect(() => {
    if (!organizationTimezone && !isLoading && !error) {
      fetchTimezone();
    }
  }, [organizationTimezone, fetchTimezone, isLoading, error]);

  const { getProfile, user } = useAuthStore();

  useEffect(() => {
    const token = auth.getToken();
    if (token && !user) {
      getProfile();
    }
  }, [getProfile, user]);

  return (
    // Relational, not a role: you are a supervisor because people report to
    // you, which no permission can express.
    <ProtectedRoute requiresSubordinates redirectTo="/login">
      <SidebarProvider>
        <AppSidebar userRole="supervisor" />
        <Outlet />
      </SidebarProvider>
    </ProtectedRoute>
  );
}
